import type { FieldDefinition } from './requests.js';

/**
 * Название документа по шаблону типа заявки: «{{route}} {{kontragent}} от {{today}}».
 * Пока автор не исправил название сам, оно собирается из маршрута и полей заявки.
 */
export const TITLE_PLACEHOLDERS = [
  { key: 'route', label: 'Название маршрута' },
  { key: 'type', label: 'Тип заявки' },
  { key: 'today', label: 'Дата создания' },
  { key: 'author', label: 'Автор' },
] as const;

/** Поля, которые годятся в название: таблицы и галочки — нет. */
export function titleFields(fields: FieldDefinition[]): FieldDefinition[] {
  return fields.filter((f) => !['table', 'checkbox', 'textarea', 'document'].includes(f.type));
}

/** Без настройки: «Название маршрута: первое поле». */
export function defaultTitlePattern(fields: FieldDefinition[]): string {
  const first = titleFields(fields)[0];
  return first ? `{{route}}: {{${first.key}}}` : '{{route}}';
}

export interface TitleLookups {
  user?: (id: string) => string | undefined;
  department?: (id: string) => string | undefined;
}

function fmtDate(v: string): string {
  return /^\d{4}-\d{2}-\d{2}/.test(v) ? `${v.slice(8, 10)}.${v.slice(5, 7)}.${v.slice(0, 4)}` : v;
}

/** Значение поля текстом — как его видит человек. */
export function fieldText(f: FieldDefinition, v: unknown, look: TitleLookups = {}): string {
  if (v === null || v === undefined || v === '') return '';
  switch (f.type) {
    case 'date':
      return typeof v === 'string' ? fmtDate(v) : '';
    case 'money':
      return typeof v === 'number' ? new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(v) : '';
    case 'number':
      return typeof v === 'number' ? String(v) : '';
    case 'user':
      return typeof v === 'string' ? (look.user?.(v) ?? '') : '';
    case 'department':
      return typeof v === 'string' ? (look.department?.(v) ?? '') : '';
    case 'table':
    case 'checkbox':
    case 'document':
      return '';
    default:
      return String(v).replace(/\s+/g, ' ').trim();
  }
}

export function titleVars(
  fields: FieldDefinition[],
  values: Record<string, unknown>,
  ctx: { route?: string | null; type?: string | null; author?: string | null; today: string },
  look: TitleLookups = {},
): Record<string, string> {
  const vars: Record<string, string> = { route: ctx.route ?? '', type: ctx.type ?? '', author: ctx.author ?? '', today: ctx.today };
  for (const f of fields) vars[f.key] = fieldText(f, values[f.key], look);
  return vars;
}

const SEP = /[\s:;,–—-]/;

/** Подставляет значения; пустые части не оставляют висящих «: » и « — ». */
export function renderTitle(pattern: string, vars: Record<string, string>): string {
  const parts = pattern.split(/(\{\{\s*[\w-]+\s*\}\})/);
  let out = '';
  for (const part of parts) {
    const m = /^\{\{\s*([\w-]+)\s*\}\}$/.exec(part);
    if (!m) {
      out += part;
      continue;
    }
    const value = vars[m[1]!] ?? '';
    if (value) out += value;
    else {
      // Пустое значение: убираем разделитель перед ним («Договор: » → «Договор»).
      let i = out.length;
      while (i > 0 && SEP.test(out[i - 1]!)) i--;
      out = out.slice(0, i);
    }
  }
  return out
    .replace(/\(\s*\)/g, '')
    .replace(/\s+/g, ' ')
    .replace(/^[\s:;,–—-]+|[\s:;,–—-]+$/g, '')
    .replace(/\s+([:;,])/g, '$1')
    .trim()
    .slice(0, 500);
}
